import { type ReactNode, type TouchEvent, useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { ChevronLeft, ChevronRight } from "@untitledui/icons";
import { useLocale } from "react-aria-components";
import { Link } from "react-router";
import { PageSection, cx, toPersianDigits } from "@mfa-aff/ui";
import type { PartnerSlideView } from "@mfa-aff/shared";
import { assetUrl } from "../../api/kit";
import { useSlides } from "../../api/panel-content";

const AUTO_ADVANCE_MS = 6000;
/** Horizontal finger travel (px) that counts as a swipe. */
const SWIPE_PX = 40;

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const subscribeReducedMotion = (onChange: () => void) => {
    const mq = window.matchMedia(REDUCED_MOTION);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
};
const usePrefersReducedMotion = () =>
    useSyncExternalStore(
        subscribeReducedMotion,
        () => window.matchMedia(REDUCED_MOTION).matches,
        () => false,
    );

const subscribeVisibility = (onChange: () => void) => {
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
};
const useTabHidden = () =>
    useSyncExternalStore(
        subscribeVisibility,
        () => document.visibilityState === "hidden",
        () => false,
    );

/** Internal panel path → router link; https → new tab; anything else is not clickable. */
const SlideLink = ({ slide, tabbable, children }: { slide: PartnerSlideView; tabbable: boolean; children: ReactNode }) => {
    const className = "block size-full outline-hidden";
    const tabIndex = tabbable ? undefined : -1;
    const url = slide.linkUrl;
    if (url?.startsWith("/")) {
        return (
            <Link to={url} className={className} tabIndex={tabIndex}>
                {children}
            </Link>
        );
    }
    if (url?.startsWith("https://")) {
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" className={className} tabIndex={tabIndex}>
                {children}
            </a>
        );
    }
    return <div className={className}>{children}</div>;
};

const SlideImage = ({ slide, eager }: { slide: PartnerSlideView; eager: boolean }) => {
    const wide = assetUrl(slide.imageUrl);
    const narrow = slide.mobileImageUrl ? assetUrl(slide.mobileImageUrl) : null;
    return (
        <>
            {/* Narrow screens without a narrow image: the wide one fits inside, over a blurred copy of itself. */}
            {!narrow && <img src={wide} alt="" aria-hidden="true" className="absolute inset-0 size-full scale-110 object-cover opacity-70 blur-xl sm:hidden" />}
            <picture>
                <source media="(min-width: 640px)" srcSet={wide} />
                <img
                    src={narrow ?? wide}
                    alt={slide.title}
                    loading={eager ? "eager" : "lazy"}
                    decoding="async"
                    draggable={false}
                    className={cx("relative block size-full select-none sm:object-cover", narrow ? "object-cover" : "object-contain")}
                />
            </picture>
        </>
    );
};

const controlClass =
    "flex cursor-pointer items-center justify-center rounded-full bg-black/35 text-white backdrop-blur-sm outline-focus-ring transition duration-100 ease-linear hover:bg-black/55 focus-visible:outline-2 focus-visible:outline-offset-2";

/** Home banner carousel (admin «اسلایدها»): nothing at all when there is no slide or the load fails. */
export const HomeSlider = () => {
    const { data } = useSlides();
    const slides = data ?? [];
    const count = slides.length;

    const { direction } = useLocale();
    const rtl = direction === "rtl";
    const reducedMotion = usePrefersReducedMotion();
    const tabHidden = useTabHidden();

    const [index, setIndex] = useState(0);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [dragPx, setDragPx] = useState(0);
    const touch = useRef<{ x: number; y: number; horizontal: boolean | null } | null>(null);
    // Images are fetched for the shown slide and the next one only, then kept.
    const [loaded, setLoaded] = useState<ReadonlySet<number>>(() => new Set([0, 1]));

    const current = count ? Math.min(index, count - 1) : 0;
    const go = useCallback(
        (i: number) => {
            if (count) setIndex(((i % count) + count) % count);
        },
        [count],
    );

    useEffect(() => {
        setLoaded((s) => (s.has(current) && s.has((current + 1) % Math.max(count, 1)) ? s : new Set([...s, current, (current + 1) % Math.max(count, 1)])));
    }, [current, count]);

    const paused = hovered || focused || tabHidden || reducedMotion || dragPx !== 0;
    useEffect(() => {
        if (count < 2 || paused) return;
        const t = window.setTimeout(() => go(current + 1), AUTO_ADVANCE_MS);
        return () => window.clearTimeout(t);
    }, [count, current, paused, go]);

    if (count === 0) return null;
    const multi = count > 1;

    // In RTL the next slide sits on the left, so the track moves right (positive X).
    const sign = rtl ? 1 : -1;

    const onTouchStart = (e: TouchEvent) => {
        if (!multi) return;
        const t = e.touches[0];
        if (t) touch.current = { x: t.clientX, y: t.clientY, horizontal: null };
    };
    const onTouchMove = (e: TouchEvent) => {
        const start = touch.current;
        const t = e.touches[0];
        if (!start || !t) return;
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;
        // Decide once per gesture whether this is a swipe or a page scroll.
        if (start.horizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) start.horizontal = Math.abs(dx) > Math.abs(dy);
        if (start.horizontal) setDragPx(dx);
    };
    const onTouchEnd = () => {
        const dx = dragPx;
        touch.current = null;
        setDragPx(0);
        if (Math.abs(dx) < SWIPE_PX) return;
        // Dragging the track towards where it moves for «next» (right in RTL) shows the next slide.
        go(dx * sign > 0 ? current + 1 : current - 1);
    };

    return (
        <PageSection>
            <section
                aria-roledescription="carousel"
                aria-label="اطلاعیه‌ها"
                className="relative isolate overflow-hidden rounded-2xl bg-secondary shadow-xs ring-1 ring-secondary ring-inset"
                onPointerEnter={(e) => e.pointerType === "mouse" && setHovered(true)}
                onPointerLeave={() => setHovered(false)}
                onFocus={() => setFocused(true)}
                onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
                }}
            >
                <div
                    className={cx(
                        "relative w-full touch-pan-y sm:aspect-[5/1]",
                        // Narrow screens use the 2:1 artwork when any slide has one; otherwise the wide ratio throughout.
                        slides.some((s) => s.mobileImageUrl) ? "aspect-[2/1]" : "aspect-[5/1]",
                    )}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onTouchCancel={onTouchEnd}
                >
                    <div
                        aria-live={paused ? "polite" : "off"}
                        className={cx("flex size-full", dragPx === 0 && "transition-transform duration-500 ease-out motion-reduce:transition-none")}
                        style={{ transform: `translateX(calc(${sign * current * 100}% + ${dragPx}px))` }}
                    >
                        {slides.map((slide, i) => {
                            const shown = i === current;
                            return (
                                <div
                                    key={slide.id}
                                    role="group"
                                    aria-roledescription="slide"
                                    aria-label={multi ? `${toPersianDigits(i + 1)} از ${toPersianDigits(count)}` : undefined}
                                    aria-hidden={shown ? undefined : true}
                                    inert={!shown}
                                    className="relative size-full shrink-0 overflow-hidden"
                                >
                                    {loaded.has(i) && (
                                        <SlideLink slide={slide} tabbable={shown}>
                                            <SlideImage slide={slide} eager={i === 0} />
                                        </SlideLink>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {multi && (
                    <>
                        <button type="button" aria-label="بنر قبلی" onClick={() => go(current - 1)} className={cx(controlClass, "absolute top-1/2 start-2 size-8 -translate-y-1/2 sm:start-3 sm:size-9")}>
                            <ChevronRight aria-hidden="true" className="size-5 ltr:-scale-x-100" />
                        </button>
                        <button type="button" aria-label="بنر بعدی" onClick={() => go(current + 1)} className={cx(controlClass, "absolute top-1/2 end-2 size-8 -translate-y-1/2 sm:end-3 sm:size-9")}>
                            <ChevronLeft aria-hidden="true" className="size-5 ltr:-scale-x-100" />
                        </button>
                    </>
                )}
            </section>
            {multi && (
                <div className="mt-2.5 flex justify-center">
                    {slides.map((slide, i) => (
                        <button
                            key={slide.id}
                            type="button"
                            aria-label={`بنر ${toPersianDigits(i + 1)}: ${slide.title}`}
                            aria-current={i === current ? "true" : undefined}
                            onClick={() => go(i)}
                            className="flex h-5 cursor-pointer items-center justify-center rounded-full px-1 outline-focus-ring focus-visible:outline-2"
                        >
                            <span
                                aria-hidden="true"
                                className={cx(
                                    "block h-1.5 rounded-full transition-all duration-300 motion-reduce:transition-none",
                                    i === current ? "w-5 bg-brand-solid" : "w-1.5 bg-quaternary hover:bg-tertiary",
                                )}
                            />
                        </button>
                    ))}
                </div>
            )}
        </PageSection>
    );
};
